/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense } from 'react';
import { Modal } from 'antd';
import { IDataModal } from '@/interfaces/dataModal';
import { IInbound } from '@/interfaces/inbound';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getInbound } from '@/adapters/inbound';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import CostCalculationTable from './cost-calculation-table';
import _ from 'lodash';
import CostCalculationBUTable from './cost-calculation-bu-table';
import ExcelExport from './export-report';
// import InboundItemTable from './inbound-item-table';

interface IProps {
  dataModal?: IDataModal<IInbound>;
  handleOk: () => void;
  handleCancel: () => void;
}

const CostCalculationModal = ({
  dataModal,
  handleOk,
  handleCancel,
}: IProps) => {
  const { data } = useQuery<AxiosResponse<IInbound>, Error>({
    queryKey: ['get-inbound', dataModal?.item],
    queryFn: async () => await getInbound(Number(dataModal?.item?.id)),
    enabled: !!dataModal?.item?.id,
  });

  const inbound = data?.data;

  const productGroups = _(inbound?.notePurchases || [])
    .groupBy(item => `${item.businessUnitId}-${item.productId}`) // Group by businessUnitId and productId
    .map((items, key) => {
      return {
        id: key,
        businessUnitName: items[0].businessUnitName, // Assuming businessUnitName is consistent
        productName: items[0].productName, // Assuming productName is consistent
        productCode: items[0].productCode,
        totalUnitCostTHB: _.sumBy(
          items,
          item => item.unitCostTHB * item.quantity,
        ),
        totalCost: _.sumBy(items, item => item.unitCost * item.quantity),
        totalQty: _.sumBy(items, item => item.quantity),
        totalFreight: _.sumBy(items, item => item.freight * item.quantity),
        totalVatCost: _.sumBy(items, item => item.vatCost * item.quantity),
        totalDuty: _.sumBy(items, item => item.duty * item.quantity),
        summaryCost: _.sumBy(items, item => item.costPerUnit * item.quantity),
        costPerUnit: items[0].costPerUnit,
      };
    })
    .value();

  const buResult = _(inbound?.notePurchases || [])
    .groupBy('businessUnitName') // Group by businessUnitId
    .map((items, businessUnitName) => ({
      businessUnitName, // Include businessUnitId
      totalUnitCostTHB: _.sumBy(
        items,
        item => item.unitCostTHB * item.quantity,
      ),
      totalFreight: _.sumBy(items, item => item.freight * item.quantity),
      totalVatCost: _.sumBy(items, item => item.vatCost * item.quantity),
      totalDuty: _.sumBy(items, item => item.duty * item.quantity),
      summaryCost: _.sumBy(items, item => item.costPerUnit * item.quantity),
    }))
    .value();

  return (
    <Modal
      title="Cost Calculation"
      open={dataModal?.open}
      onOk={() => handleOk()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={1400}
      footer={false}
    >
      <div className="flex flex-col gap-10 pb-5">
        <div className="flex flex-row gap-2 justify-end">
          <div className="basis-1/3 flex flex-col gap-1">
            <div className="flex min-w-[150px]">
              <img src={'/allied-logo.jpg'} alt="" style={{ maxHeight: 60 }} />
            </div>
          </div>
          <div className="basis-1/3 flex flex-col gap-1">
            <DetailItem label="INV GROBAL" detail={inbound?.billingNo} />
            <DetailItem label="DEC NO." detail={inbound?.decNO} />
            <DetailItem label="INVOICE NO." detail={inbound?.invNO} />
            <DetailItem
              label="INVOICE DATE"
              detail={formatDate(inbound?.invDate)}
            />
            <DetailItem
              label="DATE OF ARRIVAL"
              detail={formatDate(inbound?.arrivalDate)}
            />
          </div>
          <div className="basis-1/2">
            <div className="grid md:grid-cols-2 w-full gap-1">
              <DetailItem
                label="RATE IN MOVEX"
                detail={formatNumber(inbound?.rate || 0, 2)}
              />
              <DetailItem label="" detail={''} />
              <DetailItem label="CURRENCY" detail={'USD'} />
              <DetailItem
                customMainClass="justify-between max-w-[140px]"
                customLabelClass="md:min-w-[20px] min-w-[20px]"
                label=""
                detail={'THB'}
              />
              <DetailItem
                label="COST OF GOODS"
                detail={formatNumber(inbound?.costOfGood || 0, 2, 2)}
              />
              <DetailItem
                customMainClass="justify-between max-w-[140px]"
                customLabelClass="md:min-w-[20px] min-w-[20px]"
                label=""
                detail={formatNumber(
                  (inbound?.costOfGood || 0) * (inbound?.rate || 0),
                  2,
                )}
              />
              <DetailItem label="DUTY FEE" detail={''} />
              <DetailItem
                customMainClass="justify-between max-w-[140px]"
                customLabelClass="md:min-w-[20px] min-w-[20px]"
                label=""
                detail={formatNumber(inbound?.dutyCost || 0, 2)}
              />
              <DetailItem label="VAT" detail={''} />
              <DetailItem
                customMainClass="justify-between max-w-[140px]"
                customLabelClass="md:min-w-[20px] min-w-[20px]"
                label=""
                detail={formatNumber(inbound?.vatCost || 0, 2)}
              />
              <DetailItem label="FREIGHT" detail={''} />
              <DetailItem
                customMainClass="justify-between max-w-[140px]"
                customLabelClass="md:min-w-[20px] min-w-[20px]"
                label=""
                detail={formatNumber(inbound?.freight || 0, 2)}
              />
              <DetailItem label="TOTAL" detail={''} />
              <DetailItem
                customMainClass="justify-between max-w-[140px]"
                customLabelClass="md:min-w-[20px] min-w-[20px]"
                label=""
                detail={formatNumber(inbound?.totalCost || 0, 2)}
              />
            </div>
          </div>
        </div>

        <CostCalculationTable items={productGroups || []} inbound={inbound} />
        <CostCalculationBUTable items={buResult || []} />
        <Suspense>
          {inbound && (
            <div className="flex flex-row justify-end">
              <ExcelExport
                data={inbound}
                items={productGroups || []}
                buItems={buResult || []}
              />
            </div>
          )}
        </Suspense>
      </div>
    </Modal>
  );
};

export default CostCalculationModal;
